import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { ChatText } from '@phosphor-icons/react';
import { LazyTooltip } from '../../../components/Tooltip';
import { Icon } from '../../../components/icons/Icon';
import { LastSeenSelector } from '../../../components/LastSeenSelector';
import CustomStats from '../../../seo_components/CustomStats';
import { useViewMode, type ViewMode } from '../hooks/useViewMode';
import type { IntlShape } from 'react-intl';
import type { SrpStaticStrings, LastSeenOptionValue } from '../../../features/srp/SrpViewModel';
import {
  SRP_INLINE_SELECT_CLASSNAME,
  SRP_INLINE_SELECT_STATIC_CLASSNAME,
} from '../../../utils/srp/srp-inline-select-classname';

export interface SearchSidebarProps {
  intl?: IntlShape;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentRefinement: string;
  handleSortChange: (value: string) => void;
  hasActiveSearch: boolean;
  location: string | null;
  /** When set, company header is shown; hide duplicate stats row (e.g. "64 jobs at MongoDB"). */
  companyId?: string | null;
  className?: string;
  /** When provided (e.g. SRP static/island without SearchProvider), show simple job count instead of CustomStats. @deprecated Prefer staticStatsText + staticShowFinlandSubtitle for stable CustomStats display. */
  staticNbHits?: number;
  /** Pre-computed stats headline for CustomStats (static SRP); keeps CustomStats stable, only the string changes. */
  staticStatsText?: string;
  /** Whether to show Finland jobs subtitle when using staticStatsText. */
  staticShowFinlandSubtitle?: boolean;
  /** Pre-generated strings for static SRP (no IntlProvider). When provided, intl is not used. */
  staticStrings?: SrpStaticStrings;
  /** Resolved Last Seen option for static sidebar (from view model). */
  initialLastSeenOption?: LastSeenOptionValue;
  /** When provided, show feedback button on the stats row (right-aligned). */
  feedbackModalOpen?: boolean;
  setFeedbackModalOpen?: (open: boolean) => void;
}

/**
 * Search sidebar component - contains stats, view mode toggle, and sort / recency controls.
 * When staticStrings is provided, renders without useIntl (for static SRP).
 */
export const SearchSidebar: React.FC<SearchSidebarProps> = (props) => {
  if (props.staticStrings) {
    return <SearchSidebarWithStaticStrings {...props} />;
  }
  return <SearchSidebarWithIntl {...props} />;
};

const SEGMENT_GROUP_CLASS =
  'rounded-xl bg-[#F2EADF] dark:bg-[#2A1D0F] p-1 border border-[#DDD5C8] dark:border-[#4A3520]';

const SEGMENT_BTN_ACTIVE =
  'bg-white dark:bg-[#1F1509] text-[#3B5E45] dark:text-[#98CFA8] shadow dark:shadow-none border border-[#3B5E45] dark:border-[#98CFA8]';

const SEGMENT_BTN_INACTIVE =
  'bg-transparent text-[#3D3529] dark:text-[#C4A882] hover:bg-[#EDE3D4] dark:hover:bg-[#342415] border border-transparent';

function SearchSidebarWithStaticStrings({
  viewMode,
  setViewMode,
  currentRefinement,
  handleSortChange,
  hasActiveSearch,
  location,
  companyId = null,
  className = '',
  staticStatsText,
  staticShowFinlandSubtitle,
  staticStrings,
  initialLastSeenOption = 'all',
}: SearchSidebarProps & { staticStrings: SrpStaticStrings; initialLastSeenOption?: LastSeenOptionValue }) {
  const hideStatsRow = location || companyId;
  const isStaticSidebar = true;
  const sortMessages = staticStrings.sortLabels;
  const lastSeenValue = initialLastSeenOption ?? 'all';
  const getBaseIndexPrefix = (refinement: string): string => {
    if (refinement.endsWith('_postedDate_desc')) return refinement.replace('_postedDate_desc', '');
    if (refinement.endsWith('_salaryRangeMin_desc')) return refinement.replace('_salaryRangeMin_desc', '');
    return refinement;
  };
  const baseIndexPrefix = getBaseIndexPrefix(currentRefinement);
  const sortSegments = [
    { value: baseIndexPrefix, label: sortMessages.relevance },
    { value: `${baseIndexPrefix}_postedDate_desc`, label: sortMessages.newest },
    { value: `${baseIndexPrefix}_salaryRangeMin_desc`, label: sortMessages.salary },
  ];

  return (
    <aside
      id="search-controls"
      className={`search-controls text-[#1E1A14] dark:text-[#F2EADF] px-2 @sm:px-2 @md:px-4 pb-0 flex flex-col @sm:flex-col ${hasActiveSearch ? '' : '@[1600px]:flex-row @[1600px]:items-center @[1600px]:justify-between'} gap-2 @sm:gap-2 @md:gap-3 ${className}`}
      aria-label={staticStrings.controlsNavigation}
    >
      {/* Stats row: job count + Finland subtitle on the left, feedback button on the right (hidden when location or company header is shown) */}
      <div className={`flex items-start @[1600px]:items-center justify-between gap-3 w-full ${hasActiveSearch ? 'flex-1' : ''}`}>
        <div className={`min-w-0 break-words whitespace-normal ${hasActiveSearch ? 'flex flex-1' : 'flex-1'}`}>
          {!hideStatsRow && staticStatsText !== undefined && (
            <CustomStats statsText={staticStatsText} showFinlandSubtitle={staticShowFinlandSubtitle} finlandSubtitle={staticStrings.statsFinlandSubtitle} />
          )}
          {!hideStatsRow && staticStatsText === undefined && (
            <p className="text-sm text-[#7A6E5F] dark:text-[#B8A48C]">{staticStrings.jobCountFormatted}</p>
          )}
        </div>
        {!hideStatsRow && (
          <span
            className="inline-flex flex-shrink-0 items-center gap-2 px-4 py-2 text-sm font-medium text-[#3D3529] dark:text-[#C4A882] bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] rounded-lg shadow-sm dark:shadow-none cursor-default"
            aria-hidden
          >
            <ChatText className="w-4 h-4" />
            {staticStrings.feedbackButtonLabel}
          </span>
        )}
      </div>
      <div className={`flex flex-row flex-nowrap items-center gap-2 @sm:gap-2 @md:gap-4 justify-between w-full @sm:w-auto`}>
        <div className={`hidden @[1600px]:flex ${SEGMENT_GROUP_CLASS}`}>
          <LazyTooltip content={staticStrings.tooltipViewList} position="bottom">
            <button type="button" className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] cursor-pointer ${viewMode === 'list' ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE}`} onClick={() => setViewMode('list')} aria-pressed={viewMode === 'list'} aria-label={staticStrings.viewList}>
              <Icon name="bars4" useMaskForColor className="h-5 w-5" />
            </button>
          </LazyTooltip>
          <LazyTooltip content={staticStrings.tooltipViewMap} position="bottom">
            <button type="button" className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] cursor-pointer ${viewMode === 'map' ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE}`} onClick={() => setViewMode('map')} aria-pressed={viewMode === 'map'} aria-label={staticStrings.viewMap}>
              <Icon name="mapPin" useMaskForColor className="h-5 w-5" />
            </button>
          </LazyTooltip>
        </div>
        <div className={`flex @[1600px]:hidden ${SEGMENT_GROUP_CLASS}`}>
          <button type="button" className={`px-2 py-1.5 rounded-lg font-medium text-xs transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] cursor-pointer ${viewMode === 'list' ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE}`} onClick={() => setViewMode('list')} aria-pressed={viewMode === 'list'} aria-label={staticStrings.viewList} title={staticStrings.viewList}>
            <Icon name="bars4" useMaskForColor className="h-4 w-4" />
          </button>
          <button type="button" className={`px-2 py-1.5 rounded-lg font-medium text-xs transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] cursor-pointer ${viewMode === 'map' ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE}`} onClick={() => setViewMode('map')} aria-pressed={viewMode === 'map'} aria-label={staticStrings.viewMap} title={staticStrings.viewMap}>
            <Icon name="mapPin" useMaskForColor className="h-4 w-4" />
          </button>
        </div>
        {viewMode !== 'map' && (
        <div className="flex min-w-0 shrink items-center gap-1 @sm:gap-2 @[1600px]:hidden @[1600px]:flex-none">
          <select className={SRP_INLINE_SELECT_CLASSNAME} value={currentRefinement} onChange={(e) => handleSortChange(e.target.value)} aria-label={staticStrings.sortAriaLabel}>
            <option value={baseIndexPrefix}>{sortMessages.relevance}</option>
            <option value={`${baseIndexPrefix}_postedDate_desc`}>{sortMessages.newest}</option>
            <option value={`${baseIndexPrefix}_salaryRangeMin_desc`}>{sortMessages.salary}</option>
          </select>
          <select className={SRP_INLINE_SELECT_STATIC_CLASSNAME} value={lastSeenValue} disabled aria-label={staticStrings.lastSeenAria} tabIndex={-1}>
            {staticStrings.lastSeenOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        )}
        <div className="hidden min-w-0 items-center gap-2 @[1600px]:flex">
          {viewMode !== 'map' && (
            <div className={`flex ${SEGMENT_GROUP_CLASS}`}>
              {sortSegments.map((seg) => (
                <button key={seg.value} type="button" className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] focus:z-10 cursor-pointer ${currentRefinement === seg.value ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE}`} onClick={() => handleSortChange(seg.value)} aria-pressed={currentRefinement === seg.value}>
                  {seg.label}
                </button>
              ))}
            </div>
          )}
          <div className="min-w-0 max-w-[min(100%,14rem)] shrink-0">
            <select className={SRP_INLINE_SELECT_STATIC_CLASSNAME} value={lastSeenValue} disabled aria-label={staticStrings.lastSeenAria} tabIndex={-1}>
              {staticStrings.lastSeenOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </aside>
  );
}

function SearchSidebarWithIntl({
  intl,
  viewMode,
  setViewMode,
  currentRefinement,
  handleSortChange,
  hasActiveSearch,
  location,
  companyId = null,
  className = '',
  staticNbHits,
  staticStatsText,
  staticShowFinlandSubtitle,
  feedbackModalOpen,
  setFeedbackModalOpen,
}: SearchSidebarProps) {
  const isStaticSidebar = staticStatsText !== undefined || staticNbHits != null;
  const hideStatsRow = location || companyId;

  const sortMessages = {
    relevance: intl!.formatMessage({
      id: 'sort.relevance',
      defaultMessage: 'Relevance',
      description: 'Sort option label to sort jobs by relevance to search query',
    }),
    newest: intl!.formatMessage({
      id: 'sort.newest',
      defaultMessage: 'Newest',
      description: 'Sort option label to sort jobs by newest first',
    }),
    salary: intl!.formatMessage({
      id: 'sort.salary',
      defaultMessage: 'Salary',
      description: 'Sort option label to sort jobs by salary (highest first)',
    }),
  };

  // Extract the base index prefix from currentRefinement
  // currentRefinement can be: "en_search", "en_search_postedDate_desc", "en_search_salaryRangeMin_desc", etc.
  // We need to remove the sort suffix if present
  const getBaseIndexPrefix = (refinement: string): string => {
    if (refinement.endsWith('_postedDate_desc')) {
      return refinement.replace('_postedDate_desc', '');
    }
    if (refinement.endsWith('_salaryRangeMin_desc')) {
      return refinement.replace('_salaryRangeMin_desc', '');
    }
    return refinement;
  };

  const baseIndexPrefix = getBaseIndexPrefix(currentRefinement);

  const sortSegments = [
    { value: baseIndexPrefix, label: sortMessages.relevance },
    { value: `${baseIndexPrefix}_postedDate_desc`, label: sortMessages.newest },
    { value: `${baseIndexPrefix}_salaryRangeMin_desc`, label: sortMessages.salary },
  ];

  return (
    <aside
      id="search-controls"
      className={`search-controls text-[#1E1A14] dark:text-[#F2EADF] px-2 @sm:px-2 @md:px-4 pb-0 flex flex-col @sm:flex-col ${hasActiveSearch ? '' : '@[1600px]:flex-row @[1600px]:items-center @[1600px]:justify-between'} gap-2 @sm:gap-2 @md:gap-3 ${className}`}
      aria-label={intl!.formatMessage({
        id: 'search.controls.navigation',
        defaultMessage: 'Search controls and filters',
        description: 'Accessibility label for search controls and filter section',
      })}
    >
      {/* Stats row: job count + Finland subtitle on the left, feedback button right-aligned (hidden when location or company header is shown) */}
      <div className={`flex items-start @[1600px]:items-center justify-between gap-3 w-full ${hasActiveSearch ? 'flex-1' : ''}`}>
        <div className={`min-w-0 break-words whitespace-normal ${hasActiveSearch ? 'flex flex-1' : 'flex-1'}`}>
          {!hideStatsRow
            ? (staticStatsText !== undefined
                ? (
                    <CustomStats
                      statsText={staticStatsText}
                      showFinlandSubtitle={staticShowFinlandSubtitle}
                    />
                  )
                : staticNbHits != null
                  ? (
                      <p className="text-sm text-[#7A6E5F] dark:text-[#B8A48C]">
                        <FormattedMessage
                          id="search.jobCount"
                          defaultMessage="{count, number} jobs"
                          description="Number of job results"
                          values={{ count: staticNbHits }}
                        />
                      </p>
                    )
                  : <CustomStats />)
            : null}
        </div>
        {!hideStatsRow && setFeedbackModalOpen != null && (
          <button
            type="button"
            onClick={() => setFeedbackModalOpen(true)}
            className="inline-flex flex-shrink-0 items-center gap-2 px-4 py-2 text-sm font-medium text-[#3D3529] dark:text-[#C4A882] bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] rounded-lg hover:bg-[#F2EADF] dark:hover:bg-[#342415] transition-colors shadow-sm dark:shadow-none cursor-pointer"
          >
            <ChatText className="w-4 h-4" />
            <FormattedMessage
              id="feedback.button.label"
              defaultMessage="Give us feedback"
              description="Button label to open feedback modal"
            />
          </button>
        )}
      </div>

      {/* Main content row - contains selectors and buttons */}
      <div className={`flex flex-row flex-nowrap items-center gap-2 @sm:gap-2 @md:gap-4 justify-between w-full @sm:w-auto`}>
        {/* View mode toggle (desktop-only) */}
        <div className={`hidden @[1600px]:flex ${SEGMENT_GROUP_CLASS}`}>
          <LazyTooltip
            content={intl!.formatMessage({
              id: 'tooltip.view.list',
              defaultMessage: 'Switch to list view to see jobs in a compact list format',
              description: 'Tooltip text for list view button',
            })}
            position="bottom"
          >
            <button
              type="button"
              className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] cursor-pointer ${
                viewMode === 'list' ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE
              }`}
              onClick={() => setViewMode('list')}
              aria-pressed={viewMode === 'list'}
              aria-label={intl!.formatMessage({
                id: 'view.list',
                defaultMessage: 'List view',
                description: 'Button aria-label to switch to list view layout',
              })}
            >
              <Icon name="bars4" useMaskForColor className="h-5 w-5" />
            </button>
          </LazyTooltip>
          <LazyTooltip
            content={intl!.formatMessage({
              id: 'tooltip.view.map',
              defaultMessage: 'Switch to map view to see job locations on a map',
              description: 'Tooltip text for map view button',
            })}
            position="bottom"
          >
            <button
              type="button"
              className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] cursor-pointer ${
                viewMode === 'map' ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE
              }`}
              onClick={() => setViewMode('map')}
              aria-pressed={viewMode === 'map'}
              aria-label={intl!.formatMessage({
                id: 'view.map',
                defaultMessage: 'Map view',
                description: 'Button aria-label to switch to map view layout',
              })}
            >
              <Icon name="mapPin" useMaskForColor className="h-5 w-5" />
            </button>
          </LazyTooltip>
        </div>
        {/* View mode toggle for mobile/tablet */}
        <div className={`flex @[1600px]:hidden ${SEGMENT_GROUP_CLASS}`}>
          <button
            type="button"
            className={`px-2 py-1.5 rounded-lg font-medium text-xs transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] cursor-pointer ${
              viewMode === 'list' ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE
            }`}
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            aria-label={intl!.formatMessage({
              id: 'view.list',
              defaultMessage: 'List view',
              description: 'Button aria-label and title to switch to list view layout',
            })}
            title={intl!.formatMessage({
              id: 'view.list',
              defaultMessage: 'List view',
              description: 'Button aria-label and title to switch to list view layout',
            })}
          >
            <Icon name="bars4" useMaskForColor className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={`px-2 py-1.5 rounded-lg font-medium text-xs transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] cursor-pointer ${
              viewMode === 'map' ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE
            }`}
            onClick={() => setViewMode('map')}
            aria-pressed={viewMode === 'map'}
            aria-label={intl!.formatMessage({
              id: 'view.map',
              defaultMessage: 'Map view',
              description: 'Button aria-label and title to switch to map view layout',
            })}
            title={intl!.formatMessage({
              id: 'view.map',
              defaultMessage: 'Map view',
              description: 'Button aria-label and title to switch to map view layout',
            })}
          >
            <Icon name="mapPin" useMaskForColor className="h-4 w-4" />
          </button>
        </div>
        {/* iOS-style select for md and down - show sort only when not in map view */}
        <div className="flex min-w-0 shrink items-center gap-1 @sm:gap-2 @[1600px]:hidden @[1600px]:flex-none">
          {viewMode !== 'map' && (
            <select
              className={SRP_INLINE_SELECT_CLASSNAME}
              value={currentRefinement}
              onChange={(e) => handleSortChange(e.target.value)}
              aria-label={intl!.formatMessage({
                id: 'sort.ariaLabel',
                defaultMessage: 'Sort results',
                description: 'Accessibility label for sort dropdown to change result ordering',
              })}
            >
              <option value={baseIndexPrefix}>{sortMessages.relevance}</option>
              <option value={`${baseIndexPrefix}_postedDate_desc`}>{sortMessages.newest}</option>
              <option value={`${baseIndexPrefix}_salaryRangeMin_desc`}>{sortMessages.salary}</option>
            </select>
          )}
          {!isStaticSidebar && (
            <div className="min-w-0 max-w-[min(100%,14rem)] shrink-0">
              <LastSeenSelector />
            </div>
          )}
        </div>
        {/* iOS-style segmented control for lg and up - show sort only when not in map view */}
        <div className="hidden min-w-0 items-center gap-2 @[1600px]:flex">
          {viewMode !== 'map' && (
            <div className={`flex ${SEGMENT_GROUP_CLASS}`}>
              {sortSegments.map((seg) => (
                <button
                  key={seg.value}
                  type="button"
                  className={`px-4 py-1.5 rounded-lg font-medium text-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] focus:z-10 cursor-pointer ${
                    currentRefinement === seg.value ? SEGMENT_BTN_ACTIVE : SEGMENT_BTN_INACTIVE
                  }`}
                  onClick={() => handleSortChange(seg.value)}
                  aria-pressed={currentRefinement === seg.value}
                >
                  {seg.label}
                </button>
              ))}
            </div>
          )}
          {!isStaticSidebar && (
            <div className="hidden min-w-0 max-w-[min(100%,14rem)] shrink-0 @[1600px]:block">
              <LastSeenSelector />
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};
